import React, { useState, useEffect, useRef, memo, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getPropertiesEntry } from "../../../redux/requests";
import { falsePrewievQrValue } from "../../../redux/slices/togglePrewievQr";
import QRCodeStyling from "qr-code-styler";
import { defaultLogo } from "../qrSettings/logotypeQr/svg";
import QrTemplate from "../../../components/qrTemplate/QrTemplate";
import { setLink } from "../../../redux/slices/getQrLinkSlice";
import { setIsOverlayModal } from "../../../redux/slices/qrOverlay";
import {
  setIsDownloadSuccessModal,
  setIsPreviewMobile,
  setIsQrCodeModal,
} from "../../../redux/slices/modals";
import {
  convertImageToBase64,
  getSerializedSVGwithAnyLogo,
} from "../../../hooks/helpers";
import {
  getBase64Logo,
  getSerializedSVGwithSvgLogo,
} from "../../../hooks/helpersThirdStep";
import { Button, Col, Container, FloatingLabel, Row } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import Dropdown from "react-bootstrap/Dropdown";
import styled from "styled-components";
import {
  setDownloadQrType,
  setQrSize,
} from "../../../redux/slices/customizeQr";
import {
  qrLogotype,
  qrResetSettings,
  setBackgroundImage,
} from "../../../redux/slices/qrSettings";
import { setActiveQrTemplate } from "../../../redux/slices/qrTemplates";
import { qrResetText } from "../../../redux/slices/qrTextSettings";
import { qrResetFrame } from "../../../redux/slices/qrFrames";
import useSaveVisualization from "../../../hooks/useSaveVisualization";
import { qrOptionsReset } from "../../../redux/slices/qrOptions";
import TransparentBg from "./images/transparent-bg.png";
import { DefaultLogoContext } from "../index";
import { OverlayIcon, FilesIcon, CodeSlashIcon } from "./icons";
import EraseIcon from "../../../components/input/eraseButton/svg/EraseIcon";
import useFrames from "../../../hooks/useFrames";
import useCurrentLocation from "../../../hooks/useCurrentLocation";
import { webView, webviewEvents } from "../../../variables/webView";
import { preloaderToggle } from "../../../redux/slices/preloader";
import axios from "axios";
import useLocalStorageQrConfigs from "../../../hooks/useLocalStorageQrConfigs";
import СheckQrReadability from "../checkQrReadability";
import { designTypes } from "../../../constants/common";

export const SIZES = [
  { label: "200x200 px", size: 200 },
  { label: "500x500 px", size: 500 },
  { label: "1000x1000 px", size: 1000 },
  { label: "1500x1500 px", size: 1500 },
  { label: "2000x2000 px", size: 2000 },
];

export const FORMATS = ["png", "svg", "pdf", "eps", "jpg"];

export const convertAndDownloadQrImage = async (
  svgElement,
  qrName,
  size,
  format
) => {
  const scaleFactor = format === "pdf" ? 1.333 : 1;

  let serializedSVG = new XMLSerializer().serializeToString(svgElement);
  const qrWithLogo = svgElement.querySelector('image[href^="data:image"]');
  if (qrWithLogo) {
    serializedSVG = qrWithLogo
      .getAttribute("href")
      .startsWith("data:image/svg+xml")
      ? getSerializedSVGwithSvgLogo(serializedSVG, qrWithLogo)
      : getSerializedSVGwithAnyLogo(serializedSVG);
  }
  const base64Data = window.btoa(unescape(encodeURIComponent(serializedSVG)));
  const svgUrl = `data:image/svg+xml;base64,${base64Data}`;

  if (format === "svg") {
    const a = window.document.createElement("a");
    a.href = svgUrl;
    a.download = `${qrName}.svg`;
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
    return;
  }

  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const imgElement = new Image();
      imgElement.src = url;
      imgElement.onload = () => resolve(imgElement);
      imgElement.onerror = (err) => reject(err);
    });
  };

  try {
    const imgElement = await loadImage(svgUrl);
    const canvas = document.createElement("canvas");
    canvas.width = size * scaleFactor;
    canvas.height = size * scaleFactor;
    const ctx = canvas.getContext("2d");
    imgElement.width = size * scaleFactor;
    imgElement.height = size * scaleFactor;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Force redraw with a small delay to ensure Safari handles the image correctly
    await new Promise((resolve) =>
      setTimeout(() => {
        ctx.drawImage(imgElement, 0, 0, canvas.width, canvas.height);
        resolve();
      }, 100)
    );

    if (format === "pdf") {
      const module = await import("jspdf");
      const jsPDF = module.default;
      const jpgUrl = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "p",
        unit: "px",
        format: [size / scaleFactor, size / scaleFactor],
      });
      pdf.addImage(jpgUrl, "PNG", 0, 0);
      setTimeout(() => pdf.save(`${qrName}.${format}`), 500);
    } else if (format === "eps") {
      const { data } = await axios.post(
        "https://img-api.me-qr.com/qr/convert-svg-eps/",
        { data: base64Data },
        { responseType: "arraybuffer" }
      );
      const blob = new Blob([data], { type: "application/postscript" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${qrName}.eps`);
      document.body.appendChild(link);
      setTimeout(() => {
        link.click();
        window.URL.revokeObjectURL(url);
      }, 500);
    } else {
      const imgUrl = canvas.toDataURL(`image/${format}`);
      const link = document.createElement("a");
      link.download = `${qrName}.${format}`;
      link.href = imgUrl;
      setTimeout(() => link.click(), 500);
    }
  } catch (error) {
    console.error("Error converting and downloading QR code:", error);
  }
};

export default memo(function GeneratorQR({ top = 0, isPreviewMobile }) {
  const {
    lang,
    getQrLinkSlice,
    qrSettings,
    qrFrames,
    qrOptions,
    valueNameQr,
    qrSize,
    downloadQrType,
    typeCard,
    imgQrLink,
    mobilePreviewLang,
    designType,
  } = useSelector((state) => ({
    lang: state.changeLang.language.stepThree.generator,
    getQrLinkSlice: state.getQrLinkSlice,
    qrSettings: state.qrSettings,
    qrFrames: state.qrFrames,
    qrOptions: state.qrOptions,
    valueNameQr: state.saveValue.valueNameQr,
    qrSize: state.customizeQr.qrSize,
    downloadQrType: state.customizeQr.downloadQrType,
    typeCard: state.typeCard.type,
    imgQrLink: state.customizeQr.imgQrLink,
    mobilePreviewLang: state.changeLang.language.mobilePreview,
    designType: state.customizeQr.designType,
  }));

  const [url, setUrl] = useState("");
  let ref = useRef(null);
  let svgWrapperRef = useRef(null);
  const dispatch = useDispatch();
  const { saveVisualisation, saveTemplateVisualisation } =
    useSaveVisualization();
  const {
    defaultLogoData,
    setDefaultLogoData,
    defaultBackgroundImageData,
    setDefaultBackgroundImageData,
  } = useContext(DefaultLogoContext);
  const { entryLocation } = useCurrentLocation();
  const { getLocalConfigs } = useLocalStorageQrConfigs();

  function handlerTogglePrewiev() {
    dispatch(falsePrewievQrValue());
  }
  const qrCode = new QRCodeStyling({
    width: 300,
    height: 300,
    type: "svg",
  });

  const { frameConfigs } = useFrames();
  useEffect(() => {
    if (isPreviewMobile) return;
    if (entryLocation) {
      const getId = window.location.pathname.split("/")[2];
      if (getId === "entry") {
        dispatch(
          getPropertiesEntry({
            id: window.location.pathname.split("/")[3],
            frameConfigs,
          })
        );
      } else {
        dispatch(getPropertiesEntry({ id: getId, frameConfigs }));
      }
    } else {
      window.top !== window.self && getLocalConfigs();
    }
  }, []);

  useEffect(() => {
    setUrl(getQrLinkSlice.link);
  }, [getQrLinkSlice.link]);

  useEffect(() => {
    let newUrl = new URL(window.location);
    let c = newUrl.searchParams.get("url");
    if (c === null) {
      setUrl(getQrLinkSlice.link);
    } else {
      dispatch(setLink(c));
    }
  }, [getQrLinkSlice.link]);

  useEffect(() => {
    if (qrSettings.logotype?.includes("/qr/logo/")) {
      convertImageToBase64(qrSettings.logotype).then((data) => {
        dispatch(qrLogotype(data));
      });
    } else if (entryLocation) {
      getBase64Logo(qrSettings.logotype).then((data) =>
        setDefaultLogoData(data)
      );
    }
  }, [qrSettings.logotype]);

  useEffect(() => {
    if (qrSettings.backgroundImage?.includes("/qr/backgroundImage/")) {
      convertImageToBase64(qrSettings.backgroundImage).then((data) => {
        dispatch(setBackgroundImage(data));
      });
    } else if (entryLocation) {
      getBase64Logo(qrSettings.backgroundImage).then((data) => {
        setDefaultBackgroundImageData(data);
      });
    }
  }, [qrSettings.backgroundImage]);

  useEffect(() => {
    const {
      patternColor,
      pattern,
      cornetsOuter,
      cornetsOuterColor,
      cornetsInterior,
      cornetsInteriorColor,
      logotype,
      logotypeSize,
      logotypeMargin,
      logotypeBackground,
      isSingleColorDots,
      gradientDots,
      isSingleColorExternalEye,
      gradientExternalEye,
      isSingleColorInternalEye,
      gradientInternalEye,
      isSingleColorBackground,
      gradientBackground,
      qrBackgroundColor,
      backgroundImageSize,
      backgroundImage,
    } = qrSettings;
    const getSvg =
      logotype && defaultLogo.find((findElem) => findElem.id === logotype);
    const getBgSvg =
      backgroundImage &&
      defaultLogo.find((findElem) => findElem.id === backgroundImage);
    let svg = ref.current !== null && ref.current.querySelector("svg");
    svg && svg.remove();
    const updateQrCode = async () => {
      qrCode.update({
        data: url,
        image: getSvg ? defaultLogoData : logotype,
        dotsOptions: {
          color: patternColor,
          type: pattern,
          gradient: isSingleColorDots ? null : gradientDots,
        },
        cornersSquareOptions: {
          color: cornetsOuterColor,
          type: cornetsOuter,
          gradient: isSingleColorExternalEye ? null : gradientExternalEye,
        },
        cornersDotOptions: {
          color: cornetsInteriorColor,
          type: cornetsInterior,
          gradient: isSingleColorInternalEye ? null : gradientInternalEye,
        },
        imageOptions: {
          imageSize: logotypeSize,
          margin: logotypeMargin,
          hideBackgroundDots: logotypeBackground,
        },
        backgroundOptions: {
          gradient:
            isSingleColorBackground === false
              ? gradientBackground
              : isSingleColorBackground === true
              ? {
                  colorStops: [
                    { offset: 0, color: qrBackgroundColor },
                    { offset: 1, color: qrBackgroundColor },
                  ],
                }
              : null,
        },
        qrOptions: {
          errorCorrectionLevel: qrOptions.errorCorrectionLevel,
        },
        ...(designType === designTypes.art && {
          maskOptions: {
            drawMask: true,
            image: getBgSvg ? defaultBackgroundImageData : backgroundImage,
            imageSize: backgroundImageSize,
          },
        }),
      });

      qrCode.append(ref.current);
    };
    updateQrCode();
  }, [url, qrSettings, qrFrames, qrOptions, defaultLogoData, defaultBackgroundImageData, designType]);

  async function downloadQRcode() {
    dispatch(preloaderToggle(true));
    dispatch(setIsDownloadSuccessModal(true));
    const qrName = valueNameQr
      ? `${valueNameQr}`
      : `qrcode_${getQrLinkSlice.id}_${getQrLinkSlice.hash}`;
    const conversionData = document.querySelector("#conversion-value");
    const conversionValue =
      conversionData?.dataset[
        `value${typeCard.replace(/^\w/, (c) => c.toUpperCase())}`
      ] ||
      conversionData?.dataset.valueDefault ||
      "";
    window?.fbq && fbq("track", "DownloadQR");
    window?.uetq && uetq.push("event", "submit_lead_form", {});
    window.lintrk?.("track", { conversion_id: 22347321 });
    try {
      const locale = window.location.pathname.split("/")[1].toUpperCase()
      dataLayer.push({ event: "button_download_qr_code" });
      gtag("event", "download_qr_new_generator", {
        language: locale.length > 2 ? "EN" : locale,
      });
      gtag("event", "conversion", {
        send_to: "AW-979941213/uyeoCOCN_-wBEN3uotMD",
        value: conversionValue,
        currency: "UAH",
      });
    } catch (e) {
      console.log("error-gtag", e);
    }

    try {
      if (!!svgWrapperRef.current) {
        await convertAndDownloadQrImage(
          svgWrapperRef.current,
          qrName,
          qrSize,
          downloadQrType
        );
      }
    } catch (e) {
      console.error(e);
    } finally {
      dispatch(preloaderToggle(false));
    }
    if (window.self !== window.top) {
      window.parent.postMessage(
        {
          type: "QR_DOWNLOADED",
          payload: { message: "File downloaded successfully" },
        },
        "*"
      );
    }
    saveVisualisation();
  }

  useEffect(() => {
    if (webView && imgQrLink) {
      try {
        if (webviewEvents) {
          window.ReactNativeWebView.postMessage("app_download_qr");
        }
        window.ReactNativeWebView.postMessage(imgQrLink);
      } catch (err) {}
    }
  }, [imgQrLink]);

  function webViewDownload() {
    saveVisualisation();
  }

  const handleOverlay = () => {
    dispatch(setIsOverlayModal(true));
    handlerTogglePrewiev();
    gtag("event", "overlay_qr_code", {
      event_category: "overlay_qr_code",
      event_label: "Download QR new generator",
      value: 1,
    });
  };

  const handlehowQrCode = () => {
    dispatch(setIsQrCodeModal(true));
    saveVisualisation();
  };

  const handleSaveQrTemplate = async () => {
    await saveTemplateVisualisation();
  };

  const resetSettings = () => {
    dispatch(setActiveQrTemplate(null));
    dispatch(qrResetText());
    dispatch(qrResetSettings());
    dispatch(qrResetFrame());
    dispatch(qrOptionsReset());
  };

  return (
    <Container
      fluid={!isPreviewMobile}
      className="bg-white p-4 pb-3 sticky-lg-top rounded-1"
      style={{ top: top + "px", zIndex: 390 }}
    >
      <WrapperQr
        className="p-3 pt-2"
        isTransparent={qrSettings.patternBackgroundTransparent}
        isPreviewMobile={isPreviewMobile}
      >
        <QrTemplate wrapRef={svgWrapperRef} gRef={ref} />
      </WrapperQr>

      <СheckQrReadability svgWrapperRef={svgWrapperRef} />

      <Row className="gx-3 gy-2 mb-3 mt-2">
        <Col {...(isPreviewMobile ? { xs: 6 } : { sm: 6 })}>
          <FloatingLabel label={lang[7]}>
            <Form.Select
              value={downloadQrType}
              onChange={(e) => dispatch(setDownloadQrType(e.target.value))}
            >
              {FORMATS.map((item) => (
                <option key={item} value={item}>
                  {item.toUpperCase()}
                </option>
              ))}
            </Form.Select>
          </FloatingLabel>
        </Col>
        <Col {...(isPreviewMobile ? { xs: 6 } : { sm: 6 })}>
          <FloatingLabel label={lang[8]}>
            <Form.Select
              value={qrSize}
              onChange={(e) => dispatch(setQrSize(Number(e.target.value)))}
            >
              {SIZES.map((item) => (
                <option key={item.label} value={item.size}>
                  {item.label}
                </option>
              ))}
            </Form.Select>
          </FloatingLabel>
        </Col>
      </Row>

      {isPreviewMobile ? (
        <Row className="gx-3">
          <Col xs={6}>
            <Button
              variant="secondary"
              className="text-white w-100 fw-bold py-3 h-100"
              onClick={() => dispatch(setIsPreviewMobile(false))}
            >
              <span className="d-md-none">{mobilePreviewLang[1]}</span>
              <span className="d-none d-md-inline">{mobilePreviewLang[2]}</span>
            </Button>
          </Col>
          <Col xs={6}>
            <Button
              variant="primary"
              className="text-white w-100 fw-bold py-3 h-100"
              onClick={() => {
                dispatch(setIsPreviewMobile(false));
                webView ? webViewDownload() : downloadQRcode();
              }}
            >
              <span className="d-md-none">{mobilePreviewLang[0]}</span>
              <span className="d-none d-md-inline">{lang[1]}</span>
            </Button>
          </Col>
        </Row>
      ) : (
        <>
          <Button
            id="downloadQrCode"
            variant="primary"
            className="text-white w-100 fw-bold py-3"
            onClick={webView ? webViewDownload : downloadQRcode}
          >
            {lang[1]}
          </Button>
          <p className="fs-7 mt-2 text-gray mb-3">
            {lang[10]}{" "}
            <a
              className="cursor-pointer"
              target={"_blank"}
              href="https://me-qr.com/terms-and-conditions"
            >
              {lang[11]}
            </a>
            {lang[12]}
          </p>
        </>
      )}

      <div className="d-flex justify-content-between mt-2 flex-wrap">
        <Button
          variant="link"
          className="shadow-none text-decoration-none text-dark px-0 d-flex align-items-start text-nowrap mx-0 mx-lg-auto mx-xl-0"
          onClick={resetSettings}
        >
          <EraseIcon width={22} height={22} className="me-2" />
          {lang[3]}
        </Button>
        <Dropdown className="mx-0 mx-lg-auto mx-xl-0">
          <Dropdown.Toggle
            variant="link"
            id="dropdown-basic"
            className="shadow-none text-decoration-none text-primary px-0 d-flex align-items-center"
          >
            {lang[4]}
          </Dropdown.Toggle>

          <Dropdown.Menu>
            <DropItem onClick={handleOverlay} className="py-2">
              <OverlayIcon className="me-2" />
              {lang[2]}
            </DropItem>
            <DropItem onClick={handleSaveQrTemplate} className="py-2">
              <FilesIcon className="me-2" />
              {lang[5]}
            </DropItem>
            <DropItem onClick={handlehowQrCode} className="py-2">
              <CodeSlashIcon className="me-2" />
              {lang[6]}
            </DropItem>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </Container>
  );
});

const DropItem = styled(Dropdown.Item)`
  &:hover {
    color: #9b27af !important;
  }
`;

export const WrapperQr = styled.div`
  background: ${(props) =>
    props.isTransparent &&
    `linear-gradient(
      rgba(255, 255, 255, 0.5),
      rgba(255, 255, 255, 0.5)
    ),
    url(${TransparentBg})`};
  background-position: left top;
  background-size: cover;
  background-repeat: no-repeat;
  ${(props) =>
    props.isPreviewMobile &&
    `
    @media (min-width: 475px) {
      width: 360px;
      margin: auto;
    }
  `}
`;
